import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, map, Observable, of, shareReplay, startWith, Subscription, switchMap, tap } from 'rxjs';

import { TypeAheadItemModel } from '@ninety/mobile/platform/components';
import { ENVIRONMENT } from '@ninety/mobile/platform/environments';
import { getUserName } from '@ninety/mobile/platform/services';
import { CurrentUserSelectors, NotificationActions, UserListSelectors } from '@ninety/mobile/platform/state';

import { CreateDiscussionForm } from '../../models/form/create-discussion-form.model';
import { SeatModel } from '../../models/seats/seat.models';

/** Watches control values and enforces business logic for a given Discussion form */
@Injectable()
export class CreateDiscussionFormWatcher {
  private store = inject(Store);
  private http = inject(HttpClient);
  private readonly environment = inject(ENVIRONMENT);

  getManagerUserOptions(form: FormGroup<CreateDiscussionForm>) {
    return form.controls.manageeId.valueChanges.pipe(
      startWith(''),
      concatLatestFrom(() => this.store.select(UserListSelectors.selectActiveUsers)),
      map(([manageeId, users]) => users.filter(u => u.isConfirmed && u._id !== manageeId))
    );
  }

  getManageeUserOptions(form: FormGroup<CreateDiscussionForm>) {
    return form.controls.managerId.valueChanges.pipe(
      startWith(form.value.managerId),
      concatLatestFrom(() => [
        this.store.select(UserListSelectors.selectActiveUsers),
        this.store.select(CurrentUserSelectors.selectUser),
      ]),
      filter(([, , user]) => !!user?.company?._id),
      switchMap(([managerId, users, user]) =>
        this.getSeats(user?.company?._id as string).pipe(
          map((seats): TypeAheadItemModel[] => {
            const filteredUsers = users.filter(u => u.isConfirmed && u._id !== managerId);
            return filteredUsers.map(u => {
              const hasSeat = seats.some(s => s.seatHolders?.map(sh => sh.userId)?.includes(u._id));
              return {
                ...u,
                disabled: !hasSeat,
                name: hasSeat ? getUserName(u) : `${getUserName(u)} (Not in a Seat)`,
              };
            });
          })
        )
      )
    );
  }

  getManageeSeatOptions(form: FormGroup<CreateDiscussionForm>): Observable<TypeAheadItemModel[]> {
    return form.controls.manageeId.valueChanges.pipe(
      filter(val => !!val),
      tap(() => form.controls.manageeSeatHolderId.reset()),
      concatLatestFrom(() => this.store.select(CurrentUserSelectors.selectUser)),
      filter(([, user]) => !!user?.company?._id),
      switchMap(([selectedParticipantId, user]) =>
        this.getSeats(user?.company?._id as string).pipe(
          map(seats => {
            const userSeats = seats.filter(seat => seat.seatHolders?.some(h => h.userId === selectedParticipantId));
            return userSeats.map((seat): TypeAheadItemModel => ({
              name: seat.name,
              _id: seat.seatHolders?.find(h => h.userId === selectedParticipantId)?._id ?? '',
            }));
          }),
          tap(seats => {
            if (seats.length === 1) {
              setTimeout(() => form.controls.manageeSeatHolderId.setValue(seats[0]._id), 100);
            }
          })
        )
      ),
      startWith([])
    );
  }

  watchDiscussionForm(form: FormGroup<CreateDiscussionForm>): Subscription {
    const sub = new Subscription();
    sub.add(
      form.controls.canEvaluateManager.valueChanges.subscribe(canEvaluateManager => {
        if (canEvaluateManager) {
          form.controls.canEvaluateSelf.enable({ emitEvent: false });
        } else {
          form.controls.canEvaluateSelf.disable({ emitEvent: false });
        }
      })
    );

    sub.add(
      form.controls.canEvaluateSelf.valueChanges.subscribe(canEvaluateSelf => {
        if (canEvaluateSelf) {
          form.controls.canEvaluateManager.enable({ emitEvent: false });
        } else {
          form.controls.canEvaluateManager.disable({ emitEvent: false });
        }
      })
    );

    sub.add(
      form.controls.holdingMeeting.valueChanges.subscribe(enable => {
        if (enable) {
          form.controls.revealAnswersPriorToMeeting.enable({ emitEvent: false });
        } else {
          form.controls.revealAnswersPriorToMeeting.disable({ emitEvent: false });
        }
        form.controls.revealAnswersPriorToMeeting.setValue(true, { emitEvent: false });
      })
    );

    return sub;
  }

  private seats$: Observable<SeatModel[]> | null = null;
  getSeats(companyId: string) {
    if (!this.seats$) {
      this.seats$ = this.http
        .get<SeatModel[]>(
          `${this.environment.origin.monolith}/${companyId}/responsibilities-chart/primary/seats?includeSeatHolders=true`
        )
        .pipe(
          shareReplay(1),
          catchError(() => {
            this.store.dispatch(
              NotificationActions.showToast({
                options: {
                  type: 'negative',
                  message: 'An error occurred while loading seat holders. Please try again.',
                },
              })
            );
            return of([]);
          })
        );
    }

    return this.seats$;
  }
}
